import type { MqttAclAdapter, UserMqttCredential } from './aclSession';

interface PersonalAdapterOptions {
  token: string;
  apiUrl: string;
  brokerUrl: string;
  revision: string;
  isCurrent: () => boolean;
}

/** Resolve the user principal with the same login JWT used for minting. */
export async function createPersonalMqttAdapter(
  { token, apiUrl, brokerUrl, revision, isCurrent }: PersonalAdapterOptions,
  signal: AbortSignal
): Promise<MqttAclAdapter> {
  const api = new URL(apiUrl);
  if (api.protocol !== 'https:' || api.username || api.password || api.search || api.hash || !token) {
    throw new Error('Invalid personal MQTT API configuration');
  }
  const baseUrl = api.toString().replace(/\/$/, '');
  const request = async (path: string, method: string, requestSignal: AbortSignal): Promise<unknown> => {
    if (!isCurrent() || requestSignal.aborted) throw new Error('Personal MQTT login changed');
    const controller = new AbortController();
    const abort = () => controller.abort();
    requestSignal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 10000);
    try {
      const response = await fetch(`${baseUrl}${path}`, {
        method, signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Personal MQTT authorization failed');
      const value: unknown = await response.json();
      if (!isCurrent() || requestSignal.aborted || controller.signal.aborted) throw new Error('Personal MQTT login changed');
      return value;
    } finally {
      clearTimeout(timeout);
      requestSignal.removeEventListener('abort', abort);
    }
  };
  const me = await request('/private/user/me', 'GET', signal) as { id?: unknown; company_id?: unknown } | null;
  const validId = (value: unknown): value is string => typeof value === 'string' && !!value && !/[\u0000/+#]/.test(value);
  if (!me || !validId(me.id) || !validId(me.company_id)) throw new Error('Invalid personal MQTT principal');
  const identity = { principalId: me.id, tenantId: me.company_id, revision };
  return {
    identity, brokerUrl,
    mint: async (requestedIdentity, mintSignal) => {
      if (requestedIdentity.principalId !== identity.principalId || requestedIdentity.tenantId !== identity.tenantId ||
          requestedIdentity.revision !== revision) throw new Error('Personal MQTT identity changed');
      // AclSession validates kind, username, expiry and grants before connecting.
      return await request('/private/auth/mqtt-credentials', 'POST', mintSignal) as UserMqttCredential;
    }
  };
}
